import { Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SiTrackPage } from '../../components/si-track-page';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { columns } from './grid-columns';
import { OfficeCustomGridCell } from './office-custom-grid-cell';
import { Office } from '../../models/office';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { SelectChainItem, SelectResult } from '../../components/select/select-chain.component';
import { useSearchParams } from 'react-router-dom';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { GuardedComponent } from '../../components/guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { LoadingIndicator } from '../../components/loading-indicator.component';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
  };
};

export const OfficesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const navigate = useNavigate();

  const handleCreateProject = () => {
    navigate('create');
  };

  const handleEditOffice = (value: Office) => {
    navigate(`edit/${value.officeID}`);
  };
  const handleFilterSelected = (filter: SelectResult) => {
    setSelectedFilter(filter);

    if (filter) {
      setSearchParams({
        ...(filter.customerID && { customerID: filter.customerID }),
      });
    }
  };

  const getAPIKeyStore = () => {
    const { customerID } = selectedFilter;

    if (customerID) {
      return officeApiKeyStore.getCustomerOffices(customerID);
    }

    return officeApiKeyStore.getAllOffices();
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Office', 'Office'));

  const { isLoading, data: Offices, refetch } = useLoadData<ApiResponse<Office[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredOffices, setFilteredOffices] = useState<Office[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredOfficeList = Offices?.data?.filter((Office) =>
      Object.values(Office).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredOffices(filteredOfficeList || []);
  }, [debouncedSearchTerm, Offices?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Office',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Office',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage
      title="Offices"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_OFFICE]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
          >
            Add Office
          </Button>
        </GuardedComponent>
      }
    >
      <div className="innerContent">
        <div className="mBm">
          <SelectChainItem
            config={[
              {
                title: 'Customer',
                optionsLabelKey: 'customerName',
                optionsValueKey: 'customerID',
                type: 'autocomplete',
                getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
              },
            ]}
            value={selectedFilter}
            onSelect={handleFilterSelected}
          />
        </div>
        <Paper elevation={0} style={styling.paperStyle}>
          <SiTrackGrid
            fileTitle='office'
            dataItemKey="officeID"
            columns={columns}
            customCell={(field) =>
              OfficeCustomGridCell({
                field,
              })
            }
            // queryConfig={getAPIKeyStore()}
            viewRoute={(value) => `details/${value.officeID}`}
            onEdit={handleEditOffice}
            actionPermissions={{ edit: UserPermissionName.EDIT_OFFICE, export: UserPermissionName.DOWNLOAD_GRID }}
            isBusy={isLoadingGridLayout || isLoading}
            // -----------------Grid Layout-------
            dataItems={filteredOffices || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
            reFixPageNumber
          // -----------------Grid Layout-------
          />
        </Paper>
      </div>
    </SiTrackPage>
  );
};
